import { normalCdf } from "./normalCdf";

export function bradleyTerry(twoAfc) {
    const { sortedMethods, reorderedMatrix, matrix } = twoAfc;

    // use reordered matrix if available
    const mat = reorderedMatrix || matrix;

    const N = sortedMethods.length;
    const maxIter = 10000; // maximum number of Majorization–Minimization iterations
    const tol = 1e-9; // convergence tolerance
    const ridge = 1e-8; // stabilizer

    // win counts and total comparisons
    const W = Array.from({ length: N }, () => Array(N).fill(0));
    const NPair = Array.from({ length: N }, () => Array(N).fill(0));

    for (let i = 0; i < N; i++) {
        for (let j = 0; j < N; j++) {
            if (i === j) continue;
            const rowI = mat[i] || [];
            const rowJ = mat[j] || [];
            const vij = Number(rowI[j]) || 0;
            const vji = Number(rowJ[i]) || 0;
            W[i][j] = vij;
            NPair[i][j] = vij + vji;
        }
    }

    // Majorization–Minimization algorithm to estimate betas
    let beta = new Array(N).fill(0)

    for (let i = 0; i < N; i++) beta[i] = 0.0

    for (let iter = 0; iter < maxIter; iter++) {
        const betaOld = beta.slice()

        for (let i = 0; i < N; i++) {
            let num = 0
            let den = 0

            for (let j = 0; j < N; j++) {
                if (i === j) continue
                const nij = NPair[i][j]
                if (nij === 0) continue
                num += W[i][j]
                den += nij / (1 + Math.exp(beta[j] - beta[i]))
            }
            // update step
            if (den > 0 && num > 0) {
                beta[i] = beta[i] + Math.log(num / den)
            } else if (den > 0 && num === 0) {
                beta[i] = beta[i] - Math.log(1 + den)
            }
        }
        // center betas
        const meanBeta = beta.reduce((s, v) => s + v, 0) / N
        for (let i = 0; i < N; i++) beta[i] -= meanBeta

        // check convergence
        let maxDiff = 0
        for (let i = 0; i < N; i++) maxDiff = Math.max(maxDiff, Math.abs(beta[i] - betaOld[i]))
        if (maxDiff < tol) break
    }

    // probabilities and Fisher information matrix
    const p = Array.from({length: N}, () => Array(N).fill(0))
    const I = Array.from({length: N}, () => Array(N).fill(0))

    for (let i = 0; i < N; i++) {
        for (let j = 0; j < N; j++) {
            if (i === j) continue
            const expI = Math.exp(beta[i])
            const expJ = Math.exp(beta[j])
            p[i][j] = expI / (expI + expJ)

            const nij = NPair[i][j]
            if (nij === 0) continue

            const v = nij * p[i][j] * (1 - p[i][j])
            I[i][i] += v
            I[i][j] -= v
        }
    }

    // add ridge
    for (let i = 0; i < N; i++) I[i][i] += ridge

    const se = new Array(N).fill(NaN);
    const z = new Array(N).fill(NaN);
    for (let i = 0; i < N; i++) {
        se[i] = I[i][i] > 0 ? 1 / Math.sqrt(I[i][i]) : NaN;
        z[i] = !isNaN(se[i]) && se[i] > 0 ? beta[i] / se[i] : NaN;
    }

    // p-values
    const pvals = z.map(v =>
        isNaN(v) ? NaN : 2 * (1 - normalCdf(Math.abs(v)))
    )

    return {
        betas: beta,
        se,
        z,
        p: pvals,
        covFull: null
    }
}
